/** Papierkorb statt Löschen.
 *
 *  Zwei Menschen fassen dieselben Daten an, es gibt kein Login und kein
 *  Rückgängig im Browser. Gelöschte Bücher, Listen und Notizen bleiben deshalb
 *  30 Tage wiederherstellbar — billige Versicherung.
 */

import { useCallback, useEffect, useState } from "react";
import type { Book, BookList } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

export function Papierkorb() {
  const { owner, modus, schreibe, neuLaden } = useStore();
  const [books, setBooks] = useState<Book[]>([]);
  const [lists, setLists] = useState<BookList[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  const holen = useCallback(async () => {
    if (modus !== "server") {
      setFehler("Der Papierkorb braucht den Server.");
      setLaedt(false);
      return;
    }
    setLaedt(true);
    try {
      const res = await api.trash(owner);
      setBooks(res.data.books);
      setLists(res.data.lists);
      setFehler(null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaedt(false);
    }
  }, [owner, modus]);

  useEffect(() => {
    void holen();
  }, [holen]);

  return (
    <>
      <section style={{ maxWidth: "46ch" }}>
        <h2 className="titelzeile">Papierkorb</h2>
        <p className="lauftext">
          Was hier liegt, ist 30 Tage lang wiederherstellbar. Danach kann es
          endgültig entfernt werden — automatisch passiert das nie.
        </p>
      </section>

      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--warn)", marginTop: 20 }}
        >
          {fehler}
        </div>
      )}

      {!laedt && !fehler && !books.length && !lists.length && (
        <p className="lauftext" style={{ marginTop: 26 }}>
          Der Papierkorb ist leer.
        </p>
      )}

      {books.length > 0 && (
        <>
          <h3 className="brettname">
            Bücher <em>{books.length}</em>
          </h3>
          <div style={{ display: "grid", gap: 8 }}>
            {books.map((b) => (
              <div
                key={b.id}
                className="karte"
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  flexWrap: "wrap",
                  padding: "10px 14px",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--book)",
                    fontSize: 15,
                    flex: "1 1 240px",
                  }}
                >
                  {b.title}
                  <span className="klein" style={{ marginLeft: 10 }}>
                    {b.author} · gelöscht {b.deleted_at}
                  </span>
                </span>
                <button
                  className="knopf"
                  onClick={async () => {
                    const res = await schreibe(() =>
                      api.restoreBook(owner, b.id),
                    );
                    if (res !== null) {
                      await holen();
                      await neuLaden();
                    }
                  }}
                >
                  Zurück ins Regal
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {lists.length > 0 && (
        <>
          <h3 className="brettname">
            Listen <em>{lists.length}</em>
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {lists.map((l) => (
              <span key={l.id} className="chip">
                {l.name}
              </span>
            ))}
          </div>
          <p className="klein" style={{ marginTop: 10 }}>
            Eine gelöschte Liste kommt zurück, sobald sie unter demselben Namen
            neu angelegt wird.
          </p>
        </>
      )}

      {(books.length > 0 || lists.length > 0) && (
        <>
          <hr className="trennlinie" />
          <button
            className="knopf"
            style={{ borderColor: "rgba(217,112,102,0.5)", color: "#f0bdb7" }}
            onClick={async () => {
              const res = await schreibe(() => api.purgeTrash(owner));
              if (res) await holen();
            }}
          >
            Alles älter als 30 Tage endgültig löschen
          </button>
        </>
      )}
    </>
  );
}
